import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import {
  BehaviorSubject,
  Subject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  takeUntil
} from 'rxjs';
import { LanguageService } from '@core/i18n/language.service';

/**
 * Modelo normalizado de una categoría
 */
export interface OrgCategory {
  ORG_cat: string;
  Organization_category: string;
  ORG_sub: string;
  Organization_subcategory: string;
}

/**
 * Estructura agrupada para pintar en UI
 */
interface SearchResults {
  [categoryKey: string]: OrgCategory[];
}

@Component({
  selector: 'app-categories-search',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './categories-search.component.html',
  styleUrls: ['./categories-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CategoriesSearchComponent implements OnInit, OnDestroy, OnChanges {

  /* =========================
   * INPUTS / OUTPUTS
   * ========================= */

  @Input() tableRows: any[] = [];
  @Input() placeholder = ''; // Vacío por defecto, se generará dinámicamente
  @Input() initialSearchValue = '';
  @Input() checks: Set<string> = new Set();
  @Input() categoryField = 'map_categories'; // Campo a usar para extraer categorías

  @Output() rowsEmit = new EventEmitter<{
    rows: any[];
    selectedCategory: boolean;
    selected: Set<string>;
  }>();

  /* =========================
   * FORM CONTROL
   * ========================= */

  searchControl = new FormControl('');

  /* =========================
   * ESTADO (FUENTE DE VERDAD)
   * ========================= */

  private readonly selected$ = new BehaviorSubject<Set<string>>(new Set());
  private readonly search$ = new BehaviorSubject<string>('');

  /* =========================
   * DATOS DERIVADOS
   * ========================= */

  searchResults: SearchResults = {};
  private allCategories: OrgCategory[] = [];
  private initialized = false;
  dynamicPlaceholder = '';
  translateData: any = null;

  /* =========================
   * CLEANUP
   * ========================= */

  private destroy$ = new Subject<void>();

  constructor(private cd: ChangeDetectorRef, private langService: LanguageService) { }

  /* =========================
   * LIFECYCLE
   * ========================= */

  ngOnInit(): void {
    /**
     * Suscribirse a las traducciones
     */
    this.langService.getSharedDictionary('categories-search').pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.translateData = data;
      this.generateDynamicPlaceholder();
      this.cd.markForCheck();
    });

    /**
     * Generar placeholder dinámico 
     */
    this.generateDynamicPlaceholder();

    /**
     * Escuchamos cambios del input de búsqueda
     * y los convertimos en un stream (search$)
     */
    this.searchControl.valueChanges
      .pipe(
        startWith(this.initialSearchValue || ''),
        map(v => (v || '').trim().toLowerCase()),
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(value => this.search$.next(value));

    /**
     * Combinamos búsqueda + selección
     * para:
     *  - Pintar categorías visibles
     *  - Emitir filas filtradas
     */
    combineLatest([
      this.search$,
      this.selected$
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([search, selected]) => {
        this.updateSearchResults(search);
        this.emitFilteredRows(selected);
        this.cd.markForCheck();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tableRows'] && this.tableRows.length) {
      this.extractAllCategories();

      if (!this.initialized) {
        this.initSelection();
        this.initialized = true;
      }
    }

    if (changes['checks'] && this.checks.size) {
      this.selected$.next(new Set(this.checks));
    }

    if (changes['initialSearchValue']) {
      this.searchControl.setValue(changes['initialSearchValue'].currentValue);
    }

    if (changes['categoryField']) {
      this.generateDynamicPlaceholder();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /* =========================
   * PLACEHOLDER GENERATION
   * ========================= */

  private generateDynamicPlaceholder(): void {
    // Si el placeholder fue proporcionado explícitamente, usarlo
    if (this.placeholder && this.placeholder.trim().length > 0) {
      this.dynamicPlaceholder = this.placeholder;
      return;
    }

    // Generar placeholder basado en categoryField usando traducciones
    const fieldName = this.categoryField?.toLowerCase() || 'category';
    
    if (fieldName.includes('role')) {
      this.dynamicPlaceholder = this.translateData?.search_role || 'Search role...';
    } else if (fieldName.includes('category') || fieldName.includes('map_categories')) {
      this.dynamicPlaceholder = this.translateData?.search_category || 'Search category...';
    } else {
      this.dynamicPlaceholder = `Search ${fieldName}...`;
    }
  }

  /* =========================
   * INITIALIZATION
   * ========================= */

  /**
   * Extrae y normaliza todas las categorías únicas
   * a partir de las filas de la tabla
   */
  private extractAllCategories(): void {
    const map = new Map<string, OrgCategory>();

    this.tableRows.forEach(row => {
      const fieldData = row[this.categoryField];
      
      // Si es un array de objetos (como map_categories)
      if (Array.isArray(fieldData) && fieldData.length > 0 && typeof fieldData[0] === 'object') {
        fieldData.forEach((cat: any) => {
          if (cat?.ORG_sub) {
            map.set(cat.ORG_sub, {
              ORG_cat: cat.ORG_cat || '',
              Organization_category: cat.Organization_category || '',
              ORG_sub: cat.ORG_sub,
              Organization_subcategory: cat.Organization_subcategory || ''
            });
          }
        });
      }
      // Si es un array simple de strings (como roles)
      else if (Array.isArray(fieldData) && fieldData.length > 0 && typeof fieldData[0] === 'string') {
        fieldData.forEach((item: string) => {
          if (item && item.trim()) {
            map.set(item, {
              ORG_cat: '',
              Organization_category: 'Role',
              ORG_sub: item,
              Organization_subcategory: item
            });
          }
        });
      }
    });

    this.allCategories = Array.from(map.values());
  }

  /**
   * Selecciona todas las categorías SOLO la primera vez
   */
  private initSelection(): void {
    const initial = new Set(this.allCategories.map(c => c.ORG_sub));
    this.selected$.next(initial);
  }

  /* =========================
   * UI DERIVED STATE
   * ========================= */

  private updateSearchResults(search: string): void {
    const visible = !search
      ? this.allCategories
      : this.allCategories.filter(c =>
        c.ORG_sub.toLowerCase().includes(search) ||
        c.Organization_subcategory.toLowerCase().includes(search) ||
        c.Organization_category.toLowerCase().includes(search)
      );

    this.searchResults = visible.reduce((acc, item) => {
      const key = item.Organization_category || item.ORG_sub || 'Other';
      (acc[key] ??= []).push(item);
      return acc;
    }, {} as SearchResults);
  }

  /* =========================
   * USER ACTIONS
   * ========================= */

  onCheckboxChange(checked: boolean, key: string): void {
    const next = new Set(this.selected$.value);
    checked ? next.add(key) : next.delete(key);
    this.selected$.next(next);
  }

  onToggleSelectAll(checked: boolean): void {
    const next = new Set(this.selected$.value);

    Object.values(this.searchResults).forEach(items =>
      items.forEach(i =>
        checked ? next.add(i.ORG_sub) : next.delete(i.ORG_sub)
      )
    );

    this.selected$.next(next);
  }

  isSelected(item: OrgCategory): boolean {
    return this.selected$.value.has(item.ORG_sub);
  }

  isAllSelected(): boolean {
    const visible = Object.values(this.searchResults).flat();
    return visible.length > 0 && visible.every(item => this.selected$.value.has(item.ORG_sub));
  }

  /* =========================
   * FILTERING LOGIC
   * ========================= */

  private emitFilteredRows(selected: Set<string>): void {
    const currentSearch = this.search$.value;

    // Si no hay búsqueda activa y no hay selección, mostrar todo
    if (!currentSearch && selected.size === 0) {
      this.rowsEmit.emit({
        rows: this.tableRows,
        selectedCategory: false,
        selected
      });
      return;
    }

    // Si todas las categorías están seleccionadas y no hay búsqueda, no aplicar filtro
    if (!currentSearch && selected.size >= this.allCategories.length) {
      this.rowsEmit.emit({
        rows: this.tableRows,
        selectedCategory: false,
        selected
      });
      return;
    }

    // Si hay búsqueda pero no hay selección, usar todas las categorías visibles
    let categoriesToFilter = selected;
    if (currentSearch && selected.size === 0) {
      const visibleCategories = Object.values(this.searchResults)
        .flat()
        .map(c => c.ORG_sub);
      categoriesToFilter = new Set(visibleCategories);
    }

    // Si hay búsqueda, filtrar solo por las categorías visibles en los resultados
    if (currentSearch) {
      const visibleCategories = new Set(
        Object.values(this.searchResults)
          .flat()
          .map(c => c.ORG_sub)
      );
      categoriesToFilter = new Set(
        [...categoriesToFilter].filter(cat => visibleCategories.has(cat))
      );
    }

    const selectedLower = new Set([...categoriesToFilter].map(s => s.toLowerCase()));

    const filtered = this.tableRows.filter(row => {
      const fieldData = row[this.categoryField];

      // Para map_categories (array de objetos)
      if (Array.isArray(fieldData) && fieldData.length > 0 && typeof fieldData[0] === 'object') {
        return fieldData.some((cat: any) =>
          this.getIdentifiersFromCategory(cat)
            .some(id => selectedLower.has(id.toLowerCase()))
        );
      }

      // Para roles y otros arrays simples
      if (Array.isArray(fieldData) && fieldData.length > 0) {
        return fieldData.some((item: any) => {
          const itemStr = typeof item === 'string' ? item : (item.role_name || '');
          return selectedLower.has(itemStr.toLowerCase());
        });
      }

      return false;
    });

    this.rowsEmit.emit({
      rows: filtered,
      selectedCategory: true,
      selected: categoriesToFilter
    });
  }

  /* =========================
   * HELPERS
   * ========================= */

  private getIdentifiersFromCategory(cat: any): string[] {
    if (!cat) return [];
    return Array.from(new Set([
      cat.ORG_sub,
      cat.Organization_subcategory,
      cat.Organization_category,
      cat.ORG_cat
    ].filter(Boolean)));
  }

  getCategoryKeys(): string[] {
    return Object.keys(this.searchResults);
  }

  trackByFn(_: number, item: OrgCategory | string): string {
    return typeof item === 'string' ? item : item.ORG_sub;
  }



  hasResults(): boolean {
    return Object.keys(this.searchResults).length > 0;
  }

  idFor(prefix: string, value: string): string {
    return `${prefix}-${value}`;
  }
}
